import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import Section from '../components/Section';

/**
 * 404 with a live "tunnel scan" — animated dashed rings + a probe that pings
 * "not-found" endpoints. Themed to the brand and honest about the fact that
 * this is a routing miss, not a network error.
 */
export default function NotFound() {
  const ref = useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    // Tiny dependency-free wobble on the SVG groups, so the page has life
    // without pulling anime.js just for the 404.
    let raf = 0;
    const start = performance.now();
    const tick = (t: number) => {
      const dt = (t - start) / 1000;
      const s = ref.current;
      if (s) {
        s.querySelectorAll<SVGGElement>('[data-orbit]').forEach((g, i) => {
          const speed = 20 + i * 10;
          const angle = (dt * (360 / speed)) % 360;
          g.setAttribute('transform', `rotate(${angle} 100 100)`);
        });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <Section className="!py-28 text-center">
      <div className="chip mb-6 justify-center inline-flex">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--color-brand-glow)] animate-pulse" />
        Off the map · tunnel scan
      </div>

      <div className="relative mx-auto w-56 h-56 mb-8">
        <svg ref={ref} viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" aria-hidden>
          {[92, 74, 56, 38].map((r, i) => (
            <circle key={r} cx={100} cy={100} r={r}
              fill="none"
              stroke="rgba(232,71,106,0.35)"
              strokeWidth={i === 0 ? 1 : 0.6}
              strokeDasharray={i % 2 ? '4 6' : '2 8'} />
          ))}
          {[{ r: 56, c: '#e8476a' }, { r: 74, c: '#ffb0c1' }, { r: 92, c: '#7ce7c7' }].map((o, i) => (
            <g key={i} data-orbit>
              <circle cx={100 + o.r} cy={100} r={4} fill={o.c} />
              <circle cx={100 + o.r} cy={100} r={9} fill={o.c} opacity={0.18} />
            </g>
          ))}
        </svg>
        <div className="absolute inset-0 grid place-items-center">
          <div className="h-title text-6xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-[color:var(--color-brand)] via-[color:var(--color-brand-glow)] to-[color:var(--color-brand-deep)]">
            404
          </div>
        </div>
      </div>

      <h1 className="h-title text-3xl sm:text-4xl font-extrabold">This page took a wrong tunnel.</h1>
      <p className="mt-3 max-w-md mx-auto text-[color:var(--color-ink-dim)]">
        The route isn't in our map. Try one of these entrances instead.
      </p>

      <div className="mt-8 flex flex-wrap gap-3 justify-center">
        <Link to="/" className="btn btn-primary">Back home</Link>
        <Link to="/features" className="btn btn-ghost">Features</Link>
        <Link to="/pricing" className="btn btn-ghost">Pricing</Link>
        <Link to="/download" className="btn btn-ghost">Download</Link>
      </div>

      <p className="mt-8 text-xs text-[color:var(--color-ink-faint)] font-mono">
        GET {typeof window !== 'undefined' ? window.location.pathname : '/'} → 404 · route table miss
      </p>
    </Section>
  );
}
